<template>
  <div class="ak-display-table" :class="classList">
    <div class="ak-display-table__body">
      <slot />
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue'

export default defineComponent({
  /**
   * @type {String}
   * @description This is the name of the component this will also
   * be the tag name of the component to use in templates
   * @example
   * <ak-display-table />
   */
  name: 'AkDisplayTable',

  /**
   * @type {Object}
   * @description A collection of properties that may be passed into
   * the component these will be bound to the component instance and
   * may be access in the template and component methods.
   */
  props: {
    rows: {
      default: false,
      type: Boolean,
    },
    cosy: {
      default: false,
      type: Boolean,
    },
    hover: {
      default: false,
      type: Boolean,
    },
  },

  /**
   * @param {Object} props - The properties of the component
   * @param {Object} options - The setup options
   * @param {Function} options.emit - The emit method for triggering events
   * @param {Function} options.attrs - The attributes for the component.
   * @description Sets up the component and returns an object containing all of the
   * attributes for the component to use.
   */
  setup(props) {
    const classList = computed(() => {
      return {
        'ak-display-table--rows': props.rows,
        'ak-display-table--cosy': props.cosy,
        'ak-display-table--compact': props.compact,
        'ak-display-table--hover': props.hover,
      }
    })

    return {
      classList,
    }
  },
})
</script>

<style lang="scss">
@import '../../scss/_settings.scss';

$table-padding--compact: quarter($spacing) !default;
$table-padding--cosy: halve($spacing) !default;
$table-border-width: 1px !default;
$table-border-style: solid !default;
$table-border-color: $color-grey-lighter;
$table-hover-color: $color-grey-lightest !default;

.ak-display-table {
  $self: &;
  display: table;
  width: 100%;

  &--rows {
    #{$self}__cell {
      border-bottom: 1px solid #d3d5de;
    }
  }

  &--cosy {
    #{$self}__cell {
      padding: $table-padding--cosy;
    }
  }

  &--compact {
    #{$self}__cell {
      padding: $table-padding--compact;
    }
  }

  &--hover {
    .ak-display-table__row {
      &:hover {
        .ak-display-table__cell {
          &:not(.ak-display-table-cell--header) {
            background-color: $table-hover-color;
          }
        }
      }
    }
  }

  &__body {
    display: table-row-group;
  }

  &__row {
    display: table-row;
  }

  &__cell {
    display: table-cell;
    vertical-align: middle;

    &--header {
      color: $color-grey;
      transform: scale(0.999);
    }
  }
}
</style>
